import React from "react";
import { Handle, Position, type NodeProps } from "reactflow";
import { FiBox, FiCalendar, FiKey, FiMapPin, FiUser } from "react-icons/fi";
import type { IconType } from "react-icons";

import type { CBCNodeData, CBCType } from "../../types/ensemble.types";

// Per-type visual identity. Each Core Business Concept type gets a distinct hue
// and a monochrome Feather icon, matching the typed-node house style used by the
// data-modelling canvases (see dataMartMapping/DimensionNode, mapping/HubNode).
interface CBCTypeStyle {
  icon: IconType;
  label: string;
  // Tailwind class fragments – kept as whole class names so the JIT compiler keeps them.
  card: string;
  ring: string;
  ringSelected: string;
  iconBox: string;
  eyebrow: string;
  sourceHandle: string;
  targetHandle: string;
}

const CBC_TYPE_STYLES: Record<CBCType, CBCTypeStyle> = {
  person: {
    icon: FiUser,
    label: "Person",
    card: "tw-from-white tw-via-sky-50 tw-to-sky-100",
    ring: "tw-ring-sky-300/80 tw-border-sky-10",
    ringSelected: "tw-ring-sky-500/70 tw-border-sky-1000",
    iconBox: "tw-bg-sky-500",
    eyebrow: "tw-text-sky-700",
    sourceHandle: "!tw-bg-sky-500",
    targetHandle: "!tw-bg-sky-500 tw-border-white",
  },
  event: {
    icon: FiCalendar,
    label: "Event",
    card: "tw-from-white tw-via-amber-50 tw-to-amber-100",
    ring: "tw-ring-amber-300/80 tw-border-amber-10",
    ringSelected: "tw-ring-amber-500/70 tw-border-amber-1000",
    iconBox: "tw-bg-amber-500",
    eyebrow: "tw-text-amber-700",
    sourceHandle: "!tw-bg-amber-500",
    targetHandle: "!tw-bg-amber-500 tw-border-white",
  },
  thing: {
    icon: FiBox,
    label: "Thing",
    card: "tw-from-white tw-via-emerald-50 tw-to-emerald-100",
    ring: "tw-ring-emerald-300/80 tw-border-emerald-10",
    ringSelected: "tw-ring-emerald-500/70 tw-border-emerald-1000",
    iconBox: "tw-bg-emerald-500",
    eyebrow: "tw-text-emerald-700",
    sourceHandle: "!tw-bg-emerald-500",
    targetHandle: "!tw-bg-emerald-500 tw-border-white",
  },
  place: {
    icon: FiMapPin,
    label: "Place",
    card: "tw-from-white tw-via-violet-50 tw-to-violet-100",
    ring: "tw-ring-violet-300/80 tw-border-violet-10",
    ringSelected: "tw-ring-violet-500/70 tw-border-violet-1000",
    iconBox: "tw-bg-violet-500",
    eyebrow: "tw-text-violet-700",
    sourceHandle: "!tw-bg-violet-500",
    targetHandle: "!tw-bg-violet-500 tw-border-white",
  },
};

// Fall back to the person styling for any unexpected type value coming off the API.
const styleForType = (type: CBCType): CBCTypeStyle =>
  CBC_TYPE_STYLES[type] ?? CBC_TYPE_STYLES.person;

export const CBCNode: React.FC<NodeProps<CBCNodeData>> = ({ data }) => {
  const { cbc, isSelected, onSelect } = data;
  const style = styleForType(cbc.ensemble_model_cbc_type);
  const Icon = style.icon;

  const handleBaseClass = "!tw-h-3 !tw-w-3 !tw-border";

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(cbc.ensemble_model_cbc_id)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          onSelect(cbc.ensemble_model_cbc_id);
        }
      }}
      className={`tw-group tw-relative tw-min-w-[200px] tw-cursor-pointer tw-rounded-3xl tw-border tw-bg-gradient-to-br tw-p-4 tw-text-left tw-shadow-lg tw-transition-transform tw-duration-150 hover:tw--translate-y-1 ${style.card} ${
        isSelected
          ? `tw-ring-[3px] ${style.ringSelected}`
          : `tw-ring-2 ${style.ring}`
      }`}
    >
      <Handle
        type="target"
        position={Position.Left}
        className={`${handleBaseClass} !tw--left-1.5 ${style.targetHandle}`}
      />
      <Handle
        type="source"
        position={Position.Right}
        className={`!tw-h-3 !tw-w-3 !tw-border-2 !tw-border-white !tw--right-1.5 ${style.sourceHandle}`}
      />

      <div className="tw-flex tw-items-center tw-gap-3">
        <div
          className={`tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-2xl tw-text-white tw-shadow-md ${style.iconBox}`}
        >
          <Icon className="tw-h-4 tw-w-4" aria-hidden="true" />
        </div>
        <div className="tw-min-w-0">
          <h3 className="tw-truncate tw-text-sm tw-font-semibold tw-leading-5 tw-text-slate-900">
            {cbc.ensemble_model_cbc_name}
          </h3>
          <p className={`tw-text-[11px] tw-font-medium tw-uppercase tw-tracking-wide ${style.eyebrow}`}>
            {style.label}
            {cbc.ensemble_name ? ` · ${cbc.ensemble_name}` : ""}
          </p>
        </div>
      </div>

      {cbc.business_keys ? (
        <div className="tw-mt-3 tw-flex tw-items-center tw-gap-2 tw-border-t tw-border-black/5 tw-pt-2.5 tw-font-mono tw-text-[12px] tw-text-slate-600">
          <FiKey className="tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-text-slate-400" aria-hidden="true" />
          <span className="tw-truncate" title={cbc.business_keys}>
            {cbc.business_keys}
          </span>
        </div>
      ) : null}
    </div>
  );
};

export default CBCNode;
